import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import SideBar from "../../components/SideBar/SideBar";
import TopNav from "../../components/TopNav/TopNav";
import api from "../../services/api";
import "../Users/Users.scss";
import { format } from "date-fns";
import profileDefault from "../../assets/images/profileDefault.png";

// Surfaces every user whose deleted_at falls inside the configured
// retention window (defaults to 30 days). Lets support audit reasons,
// spot bug-driven spikes ("5 deletes in the last 2h all citing
// 'Bugs / app didn't work'"), and decide whether to reach out before
// the row is hard-deleted by the daily reaper cron.
const DeletedUsers = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("deleted"); // 'deleted' | 'banned'
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [windowDays, setWindowDays] = useState(30);
  const [searchTerm, setSearchTerm] = useState("");
  // Banned tab: no retention window (bans are permanent), so just a
  // flat list, fetched once when the tab is first opened.
  const [bannedUsers, setBannedUsers] = useState([]);
  const [bannedLoading, setBannedLoading] = useState(true);
  // Tracks which row is mid-restore so a double-click can't fire the
  // POST twice (server is idempotent but the second confirm prompt is
  // a confusing UX).
  const [restoringId, setRestoringId] = useState(null);
  // Which copy target ("id-<_id>" / "email-<_id>") just fired, for the ✓ flash.
  const [copiedKey, setCopiedKey] = useState(null);

  // Rows navigate on click, so copy targets must stopPropagation. The
  // window.prompt fallback covers non-secure contexts where the
  // Clipboard API is unavailable.
  const handleCopy = useCallback((e, key, value) => {
    e.stopPropagation();
    if (!value) return;
    const flash = () => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1200);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(value).then(flash, () => window.prompt("Copy manually:", value));
    } else {
      window.prompt("Copy manually:", value);
    }
  }, []);

  const fetchDeleted = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/users/admin/deleted-recent`, {
        params: { days: windowDays },
      });
      setUsers(data?.data || []);
    } catch (err) {
      console.error("Failed to fetch deleted users:", err);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [windowDays]);

  // Calls the admin-only restore endpoint that clears deleted_at + re-
  // enables the Firebase Auth user. Works only inside the retention
  // window — the reaper has already hard-deleted anything older.
  const handleRestore = useCallback(
    async (e, userId) => {
      e.stopPropagation(); // don't trigger the row navigation
      if (restoringId) return;
      if (!window.confirm("Restore this account? The user will be able to sign in again.")) return;
      setRestoringId(userId);
      try {
        // 15s timeout so a Firebase API stall doesn't leave the
        // button stuck disabled with no recourse short of a reload.
        await api.post(`/users/admin/restore/${userId}`, undefined, { timeout: 15000 });
        // Drop the row locally so the table reflects state without
        // waiting for the next fetch tick. Filter by _id only (matches
        // the React key) to avoid partial-collision bugs where an empty
        // `id` on one row matches an `_id` on another.
        setUsers(prev => prev.filter(u => u._id !== userId));
      } catch (err) {
        console.error("Restore failed:", err);
        const msg = err?.code === 'ECONNABORTED'
          ? 'Restore timed out. Try again.'
          : (err?.response?.data?.message || 'Restore failed. Try again.');
        window.alert(msg);
      } finally {
        setRestoringId(null);
      }
    },
    [restoringId],
  );

  useEffect(() => {
    fetchDeleted();
  }, [fetchDeleted]);

  const fetchBanned = useCallback(async () => {
    try {
      setBannedLoading(true);
      const { data } = await api.get(`/users/admin/banned-recent`);
      setBannedUsers(data?.data || []);
    } catch (err) {
      console.error("Failed to fetch banned users:", err);
      setBannedUsers([]);
    } finally {
      setBannedLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === "banned") fetchBanned();
  }, [tab, fetchBanned]);

  const formatDate = (d) => {
    if (!d) return "—";
    try {
      return format(new Date(d), "MMM d, yyyy h:mm a");
    } catch {
      return "—";
    }
  };

  const REASON_LABELS = {
    not_using: { label: "Not using", color: "#6B7280" },
    bug: { label: "Bug / app didn't work", color: "#EF4444" },
    privacy: { label: "Privacy", color: "#F59E0B" },
    too_expensive: { label: "Too expensive", color: "#0A3FD6" },
    other: { label: "Other", color: "#4F30EA" },
  };

  const renderReason = (reason) => {
    if (!reason) return <span style={{ opacity: 0.4 }}>—</span>;
    const meta = REASON_LABELS[reason] || { label: reason, color: "#6B7280" };
    return (
      <span
        style={{
          background: meta.color,
          color: "#fff",
          fontSize: 11,
          fontWeight: 600,
          padding: "3px 8px",
          borderRadius: 999,
          whiteSpace: "nowrap",
        }}
      >
        {meta.label}
      </span>
    );
  };

  const filtered = users.filter((u) => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      (u.firstName || "").toLowerCase().includes(s) ||
      (u.lastName || "").toLowerCase().includes(s) ||
      (u.email || "").toLowerCase().includes(s) ||
      (u.id || "").toLowerCase().includes(s) ||
      (u.delete_reason_notes || "").toLowerCase().includes(s)
    );
  });

  const filteredBanned = bannedUsers.filter((u) => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      (u.firstName || "").toLowerCase().includes(s) ||
      (u.lastName || "").toLowerCase().includes(s) ||
      (u.email || "").toLowerCase().includes(s) ||
      (u.id || "").toLowerCase().includes(s) ||
      (u.ban_reason || "").toLowerCase().includes(s)
    );
  });

  const bannedByLabel = (b) => {
    if (!b) return "—";
    if (typeof b === "string") return b;
    return `${b.firstName || ""} ${b.lastName || ""}`.trim() || b.email || "—";
  };

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content" style={{ padding: 24 }}>
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <div>
              <h1 style={{ margin: 0, color: "#fff" }}>Deleted Users</h1>
              <p style={{ margin: "4px 0 0", color: "rgba(255,255,255,0.5)", fontSize: 13 }}>
                {tab === "deleted"
                  ? `Soft-deleted accounts inside the ${windowDays}-day retention window. Hard-deleted by the daily reaper at 3am Miami after retention expires.`
                  : "Accounts banned for cause. Permanent until an admin reverses it — no retention window or reaper."}
              </p>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="text"
                placeholder={tab === "deleted" ? "Search name, email, notes…" : "Search name, email, reason…"}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "#fff",
                  padding: "8px 12px",
                  borderRadius: 8,
                  fontSize: 13,
                  outline: "none",
                  width: 240,
                }}
              />
              {tab === "deleted" && (
                <select
                  value={windowDays}
                  onChange={(e) => setWindowDays(Number(e.target.value))}
                  style={{
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    color: "#fff",
                    padding: "8px 12px",
                    borderRadius: 8,
                    fontSize: 13,
                    outline: "none",
                  }}
                >
                  <option value={1}>Last 24 hours</option>
                  <option value={7}>Last 7 days</option>
                  <option value={30}>Last 30 days</option>
                </select>
              )}
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            <button
              type="button"
              onClick={() => setTab("deleted")}
              style={{ borderRadius: 10, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", border: tab === "deleted" ? "none" : "1px solid rgba(255,255,255,.15)", background: tab === "deleted" ? "linear-gradient(135deg,#0A3FD6,#7229F5)" : "transparent", color: tab === "deleted" ? "#fff" : "rgba(255,255,255,.6)" }}
            >
              Deleted
            </button>
            <button
              type="button"
              onClick={() => setTab("banned")}
              style={{ borderRadius: 10, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", border: tab === "banned" ? "none" : "1px solid rgba(255,255,255,.15)", background: tab === "banned" ? "linear-gradient(135deg,#0A3FD6,#7229F5)" : "transparent", color: tab === "banned" ? "#fff" : "rgba(255,255,255,.6)" }}
            >
              Banned
            </button>
          </div>

          {tab === "deleted" ? (
          <div
            style={{
              width: "100%",
              background: "rgba(255,255,255,0.03)",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.06)",
              // Scroll a wide table inside its own box. Without this the
              // .page-content flex container (align-items:center) center-
              // overflows the table, sliding its left columns under the
              // fixed sidebar. See fix/deleted-users-table-overflow.
              overflowX: "auto",
              overflowY: "hidden",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse", color: "#fff", minWidth: 900 }}>
              <thead>
                <tr style={{ background: "rgba(255,255,255,0.04)" }}>
                  <th style={{ ...th, width: 44 }}></th>
                  <th style={th}>User</th>
                  <th style={th}>User ID</th>
                  <th style={th}>Email</th>
                  <th style={th}>Deleted</th>
                  <th style={th}>Days remaining</th>
                  <th style={th}>Reason</th>
                  <th style={th}>App version</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} style={{ ...td, textAlign: "center", padding: 32, opacity: 0.6 }}>
                      Loading…
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ ...td, textAlign: "center", padding: 32, opacity: 0.6 }}>
                      No deletions in this window.
                    </td>
                  </tr>
                ) : (
                  filtered.map((u) => (
                    <tr
                      key={u._id || u.id}
                      onClick={() => navigate(`/deleted-users/${u._id}`, { state: { row: u } })}
                      style={{ cursor: "pointer", borderTop: "1px solid rgba(255,255,255,0.05)" }}
                    >
                      <td style={{ ...td, textAlign: "center" }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/deleted-users/${u._id}`, { state: { row: u } });
                          }}
                          title="View deletion details, profile & activity"
                          aria-label="View details"
                          style={eyeBtn}
                        >
                          <EyeIcon />
                        </button>
                      </td>
                      <td style={td}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <img
                            src={u.photo_url || profileDefault}
                            alt=""
                            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = profileDefault; }}
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 16,
                              objectFit: "cover",
                              background: "#222",
                            }}
                          />
                          <span>{`${u.firstName || ""} ${u.lastName || ""}`.trim() || "—"}</span>
                        </div>
                      </td>
                      <td style={td}>
                        {u.id ? (
                          <button
                            onClick={(e) => handleCopy(e, `id-${u._id}`, u.id)}
                            title={`${u.id} — click to copy`}
                            style={copyChip}
                          >
                            <span style={{ fontFamily: "monospace" }}>
                              {copiedKey === `id-${u._id}` ? "Copied ✓" : `${u.id.slice(0, 10)}…`}
                            </span>
                          </button>
                        ) : (
                          <span style={{ opacity: 0.4 }}>—</span>
                        )}
                      </td>
                      <td style={td}>
                        {u.email ? (
                          <button
                            onClick={(e) => handleCopy(e, `email-${u._id}`, u.email)}
                            title="Click to copy"
                            style={{ ...copyChip, fontFamily: "inherit" }}
                          >
                            {copiedKey === `email-${u._id}` ? "Copied ✓" : u.email}
                          </button>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td style={td}>{formatDate(u.deleted_at)}</td>
                      <td style={td}>
                        <span
                          style={{
                            color:
                              u.days_remaining <= 1
                                ? "#EF4444"
                                : u.days_remaining <= 3
                                ? "#F59E0B"
                                : "#fff",
                            fontWeight: u.days_remaining <= 3 ? 700 : 400,
                          }}
                        >
                          {u.days_remaining <= 1 ? "Final 24h" : u.days_remaining}
                        </span>
                      </td>
                      <td style={td}>{renderReason(u.delete_reason)}</td>
                      <td style={td}>{u.app_version || "—"}</td>
                      <td style={{ ...td, textAlign: "right" }}>
                        <button
                          onClick={(e) => handleRestore(e, u._id)}
                          disabled={restoringId === u._id}
                          style={{
                            background: restoringId === u._id ? "#555" : "#0A3FD6",
                            color: "#fff",
                            border: "none",
                            padding: "6px 12px",
                            borderRadius: 6,
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: restoringId === u._id ? "not-allowed" : "pointer",
                            opacity: restoringId === u._id ? 0.7 : 1,
                          }}
                        >
                          {restoringId === u._id ? "Restoring…" : "Restore"}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          ) : (
          <div
            style={{
              width: "100%",
              background: "rgba(255,255,255,0.03)",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.06)",
              overflowX: "auto",
              overflowY: "hidden",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse", color: "#fff", minWidth: 900 }}>
              <thead>
                <tr style={{ background: "rgba(255,255,255,0.04)" }}>
                  <th style={{ ...th, width: 44 }}></th>
                  <th style={th}>User</th>
                  <th style={th}>Email</th>
                  <th style={th}>Banned</th>
                  <th style={th}>Reason</th>
                  <th style={th}>Banned by</th>
                </tr>
              </thead>
              <tbody>
                {bannedLoading ? (
                  <tr>
                    <td colSpan={6} style={{ ...td, textAlign: "center", padding: 32, opacity: 0.6 }}>
                      Loading…
                    </td>
                  </tr>
                ) : filteredBanned.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ ...td, textAlign: "center", padding: 32, opacity: 0.6 }}>
                      No banned accounts.
                    </td>
                  </tr>
                ) : (
                  filteredBanned.map((u) => (
                    <tr
                      key={u._id || u.id}
                      onClick={() => navigate(`/users/edit/${u.id || u._id}`)}
                      style={{ cursor: "pointer", borderTop: "1px solid rgba(255,255,255,0.05)" }}
                    >
                      <td style={{ ...td, textAlign: "center" }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/users/edit/${u.id || u._id}`);
                          }}
                          title="View profile & ban status"
                          aria-label="View profile"
                          style={eyeBtn}
                        >
                          <EyeIcon />
                        </button>
                      </td>
                      <td style={td}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <img
                            src={u.photo_url || profileDefault}
                            alt=""
                            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = profileDefault; }}
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 16,
                              objectFit: "cover",
                              background: "#222",
                            }}
                          />
                          <span>{`${u.firstName || ""} ${u.lastName || ""}`.trim() || "—"}</span>
                        </div>
                      </td>
                      <td style={td}>{u.email || "—"}</td>
                      <td style={td}>{formatDate(u.banned_at)}</td>
                      <td style={td}>{u.ban_reason || "—"}</td>
                      <td style={td}>{bannedByLabel(u.banned_by)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          )}
        </article>
      </div>
    </main>
  );
};

const th = {
  textAlign: "left",
  padding: "12px 14px",
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 1.5,
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.6)",
};
const td = {
  padding: "12px 14px",
  fontSize: 13,
  verticalAlign: "middle",
};
const copyChip = {
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.12)",
  color: "#fff",
  padding: "4px 8px",
  borderRadius: 6,
  fontSize: 12,
  cursor: "copy",
  whiteSpace: "nowrap",
};
const eyeBtn = {
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.12)",
  color: "#fff",
  width: 32,
  height: 32,
  borderRadius: 8,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
};

// Inline SVG so we don't take a new icon-lib dependency on this page.
const EyeIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

export default DeletedUsers;
